import { useEffect, useState } from 'react';

import { API_PRODUCTS } from 'Constants';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';

const batchCache = new Map();
const inFlightRequests = new Map();

const normalizeProduct = (product, productId) => ({
  ...product,
  id: product?.id || productId,
  appliedPromotions: product?.appliedPromotions || [],
  hasPrice: product?.hasPrice ?? product?.price !== null,
  minQuantity: product?.minQuantity || 1,
  pictures: product?.pictures || [],
  priceScales: product?.priceScales || [],
  promotions: product?.promotions || {},
  quantity: product?.quantity || product?.minQuantity || 1,
  warnings: product?.warnings || {},
});

const normalizeResponse = (data, productIds) => {
  const responseProducts = data?.products || data;

  if (!responseProducts || typeof responseProducts !== 'object') return [];

  if (Array.isArray(responseProducts)) {
    const productsById = new Map(responseProducts.map(product => [product.id, product]));
    return productIds.flatMap(productId => {
      const product = productsById.get(productId);
      return product?.code && product?.title ? [normalizeProduct(product, productId)] : [];
    });
  }

  return productIds.flatMap(productId => {
    const product = responseProducts[productId];
    return product?.code && product?.title ? [normalizeProduct(product, productId)] : [];
  });
};

const fetchProducts = (fetcher, cacheKey, url, productIds) => {
  if (batchCache.has(cacheKey)) return Promise.resolve(batchCache.get(cacheKey));
  if (inFlightRequests.has(cacheKey)) return inFlightRequests.get(cacheKey);

  const request = fetcher({ url }, { noCancel: true })
    .then(data => {
      const products = normalizeResponse(data, productIds);
      batchCache.set(cacheKey, products);
      return products;
    })
    .finally(() => inFlightRequests.delete(cacheKey));

  inFlightRequests.set(cacheKey, request);
  return request;
};

const useTemplateProducts = productIds => {
  const fetcher = useFetch();
  const { data: user } = useUser();
  const { email, defaultCurrency: currency, withVat: vat } = user || {};
  const ids = productIds.filter(Boolean);
  const idsKey = ids.join(',');
  const uniqueIds = [...new Set(ids)];
  const params = new URLSearchParams();
  params.set('currency', currency || 'RON');
  params.set('vat', String(vat ?? false));
  params.set('only_valid', 1);
  uniqueIds.forEach(productId => params.append('productIds[]', productId));
  const url = uniqueIds.length
    ? `${API_PRODUCTS}/offer-products-details?${params.toString()}`
    : '';
  const cacheKey = email && url
    ? `${email}|${currency || 'RON'}|${String(vat ?? false)}|${idsKey}`
    : '';
  const [products, setProducts] = useState(() => batchCache.get(cacheKey) || []);
  const [loading, setLoading] = useState(() => Boolean(cacheKey) && !batchCache.has(cacheKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    if (!cacheKey) {
      setProducts([]);
      setError(null);
      setLoading(false);
      return () => { active = false; };
    }

    const cachedProducts = batchCache.get(cacheKey);
    if (cachedProducts) {
      setProducts(cachedProducts);
      setError(null);
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    setError(null);
    const requestedIds = idsKey.split(',').filter(Boolean);
    fetchProducts(fetcher, cacheKey, url, requestedIds)
      .then(data => {
        if (active) setProducts(data);
      })
      .catch(requestError => {
        if (!active) return;

        const availableProducts = normalizeResponse(requestError.info, requestedIds);
        setProducts(availableProducts);
        setError(availableProducts.length ? null : requestError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [cacheKey, fetcher, idsKey, url]);

  return { products, loading, error };
};

export default useTemplateProducts;
